import React, { useState, useRef, useEffect } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { LIST_CUSTOM_EVALUATION_RULES } from '../../../graphql/queries';
import {
  CREATE_CUSTOM_EVALUATION_RULE,
  UPDATE_CUSTOM_EVALUATION_RULE,
  DELETE_CUSTOM_EVALUATION_RULE,
  UPDATE_CUSTOM_EVALUATION_RULES_ORDER
} from '../../../graphql/mutations';
import { Scenario, CustomEvaluationRule } from '../../../types/models';
import { CUSTOM_EVALUATION_RULE_VALIDATIONS, validateCustomEvaluationRule } from '../../../utils/validationUtils';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';

interface CustomEvaluationRulesDialogProps {
  open: boolean;
  scenario: Scenario | null;
  companyId: string;
  onClose: () => void;
  onRulesUpdated?: (scenarioId: string, count: number) => void;
}

const CustomEvaluationRulesDialog: React.FC<CustomEvaluationRulesDialogProps> = ({
  open,
  scenario,
  companyId,
  onClose,
  onRulesUpdated
}) => {
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [customEvaluationRules, setCustomEvaluationRules] = useState<CustomEvaluationRule[]>([]);
  const [customEvaluationLoading, setCustomEvaluationLoading] = useState(false);
  const [customEvaluationError, setCustomEvaluationError] = useState<string | null>(null);
  const [editingRule, setEditingRule] = useState<CustomEvaluationRule | null>(null);
  const [isAddingRule, setIsAddingRule] = useState(false);
  const [ruleFormData, setRuleFormData] = useState({ name: '', content: '' });

  const [getCustomEvaluationRules] = useLazyQuery(LIST_CUSTOM_EVALUATION_RULES);
  const [createCustomEvaluationRule] = useMutation(CREATE_CUSTOM_EVALUATION_RULE);
  const [updateCustomEvaluationRule] = useMutation(UPDATE_CUSTOM_EVALUATION_RULE);
  const [deleteCustomEvaluationRule] = useMutation(DELETE_CUSTOM_EVALUATION_RULE);
  const [updateCustomEvaluationRulesOrder] = useMutation(UPDATE_CUSTOM_EVALUATION_RULES_ORDER);

  useEffect(() => {
    if (!open || !scenario) return;
    setCustomEvaluationError(null);
    setEditingRule(null);
    setIsAddingRule(false);
    setRuleFormData({ name: '', content: '' });
    setCustomEvaluationLoading(true);
    getCustomEvaluationRules({ variables: { parentScenarioId: scenario.scenarioId, limit: 100 } })
      .then(({ data }) => {
        if (data?.listCustomEvaluationRules?.items) {
          const sorted = [...data.listCustomEvaluationRules.items].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
          setCustomEvaluationRules(sorted);
        } else {
          setCustomEvaluationRules([]);
        }
      })
      .catch((err) => {
        console.error(err);
        setCustomEvaluationError('カスタム評価ルールの取得中にエラーが発生しました。');
        setCustomEvaluationRules([]);
      })
      .finally(() => setCustomEvaluationLoading(false));
  }, [open, scenario, getCustomEvaluationRules]);

  const refreshRules = async () => {
    if (!scenario) return;
    const { data } = await getCustomEvaluationRules({
      variables: { parentScenarioId: scenario.scenarioId, limit: 100 }
    });
    if (data?.listCustomEvaluationRules?.items) {
      const sorted = [...data.listCustomEvaluationRules.items].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
      setCustomEvaluationRules(sorted);
      onRulesUpdated?.(scenario.scenarioId, sorted.length);
    }
  };

  const handleAddRule = () => {
    setEditingRule(null);
    setIsAddingRule(true);
    setRuleFormData({ name: '', content: '' });
    setTimeout(() => nameInputRef.current?.focus(), 0);
  };

  const handleEditRule = (rule: CustomEvaluationRule) => {
    setEditingRule(rule);
    setRuleFormData({ name: rule.name, content: rule.content });
    setTimeout(() => nameInputRef.current?.focus(), 0);
  };

  const handleLoadFromFile = () => {
    if (!scenario) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt,.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      setCustomEvaluationError(null);
      setCustomEvaluationLoading(true);
      try {
        const arrayBuffer = await file.arrayBuffer();
        let text: string;
        let data: { customEvaluationRules?: Array<{ name?: unknown; content?: unknown }> };
        try {
          text = new TextDecoder('utf-8').decode(arrayBuffer);
          data = JSON.parse(text) as { customEvaluationRules?: Array<{ name?: unknown; content?: unknown }> };
        } catch {
          try {
            text = new TextDecoder('shift_jis').decode(arrayBuffer);
            data = JSON.parse(text) as { customEvaluationRules?: Array<{ name?: unknown; content?: unknown }> };
          } catch {
            setCustomEvaluationError('サポートされていない文字エンコードまたはファイルの形式が正しくありません。');
            setCustomEvaluationLoading(false);
            return;
          }
        }
        if (!data.customEvaluationRules || !Array.isArray(data.customEvaluationRules)) {
          setCustomEvaluationError('ファイルにcustomEvaluationRules配列が見つかりません。');
          setCustomEvaluationLoading(false);
          return;
        }
        const currentCount = customEvaluationRules.length;
        const availableSlots = Math.max(0, 10 - currentCount);
        if (availableSlots <= 0) {
          setCustomEvaluationError('カスタム評価ルールは1シナリオにつき最大10件まで登録できます。既に上限に達しています。');
          setCustomEvaluationLoading(false);
          return;
        }
        const rulesToAdd = data.customEvaluationRules.slice(0, availableSlots);
        if (data.customEvaluationRules.length > availableSlots) {
          setCustomEvaluationError(`${data.customEvaluationRules.length}件のルールが見つかりましたが、${availableSlots}件のみ登録しました（上限10件）。`);
        }
        let addedCount = 0;
        for (let i = 0; i < rulesToAdd.length; i++) {
          const rule = rulesToAdd[i];
          if (!rule.name || !rule.content) continue;
          const validation = validateCustomEvaluationRule({
            name: String(rule.name).trim(),
            content: String(rule.content).trim()
          });
          if (!validation.isValid) continue;
          try {
            await createCustomEvaluationRule({
              variables: {
                input: {
                  companyId,
                  parentScenarioId: scenario.scenarioId,
                  name: String(rule.name).trim(),
                  content: String(rule.content).trim(),
                  displayOrder: currentCount + addedCount
                }
              }
            });
            addedCount++;
          } catch {
            // continue
          }
        }
        await refreshRules();
      } catch (err) {
        console.error(err);
        setCustomEvaluationError('カスタム評価ルールの読み込み中にエラーが発生しました。');
      } finally {
        setCustomEvaluationLoading(false);
      }
    };
    input.click();
  };

  const handleSaveRule = async () => {
    if (!scenario) return;
    const validation = validateCustomEvaluationRule({ name: ruleFormData.name, content: ruleFormData.content });
    if (!validation.isValid) {
      setCustomEvaluationError(validation.errors.map((e) => e.message).join('\n'));
      return;
    }
    if (!editingRule && customEvaluationRules.length >= 10) {
      setCustomEvaluationError('カスタム評価ルールは1シナリオにつき最大10件まで登録できます。');
      return;
    }
    setCustomEvaluationLoading(true);
    setCustomEvaluationError(null);
    try {
      if (editingRule) {
        await updateCustomEvaluationRule({
          variables: {
            input: {
              ruleId: editingRule.ruleId,
              name: ruleFormData.name.trim(),
              content: ruleFormData.content.trim()
            }
          }
        });
      } else {
        await createCustomEvaluationRule({
          variables: {
            input: {
              companyId,
              parentScenarioId: scenario.scenarioId,
              name: ruleFormData.name.trim(),
              content: ruleFormData.content.trim(),
              displayOrder: customEvaluationRules.length
            }
          }
        });
      }
      await refreshRules();
      setEditingRule(null);
      setIsAddingRule(false);
      setRuleFormData({ name: '', content: '' });
    } catch (err) {
      console.error(err);
      setCustomEvaluationError('カスタム評価ルールの保存中にエラーが発生しました。');
    } finally {
      setCustomEvaluationLoading(false);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!scenario || !window.confirm('このカスタム評価ルールを削除しますか？')) return;
    setCustomEvaluationLoading(true);
    setCustomEvaluationError(null);
    try {
      await deleteCustomEvaluationRule({ variables: { ruleId } });
      await refreshRules();
    } catch (err) {
      console.error(err);
      setCustomEvaluationError('カスタム評価ルールの削除中にエラーが発生しました。');
    } finally {
      setCustomEvaluationLoading(false);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || !scenario || editingRule !== null || isAddingRule) return;
    const items = Array.from(customEvaluationRules);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    setCustomEvaluationRules(items);
    setCustomEvaluationLoading(true);
    setCustomEvaluationError(null);
    try {
      await updateCustomEvaluationRulesOrder({
        variables: {
          input: {
            parentScenarioId: scenario.scenarioId,
            ruleOrders: items.map((r, i) => ({ ruleId: r.ruleId, displayOrder: i }))
          }
        }
      });
      const { data } = await getCustomEvaluationRules({
        variables: { parentScenarioId: scenario.scenarioId, limit: 100 }
      });
      if (data?.listCustomEvaluationRules?.items) {
        const sorted = [...data.listCustomEvaluationRules.items].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
        setCustomEvaluationRules(sorted);
      }
    } catch (err) {
      console.error(err);
      setCustomEvaluationError('カスタム評価ルールの順序保存中にエラーが発生しました。');
      const { data } = await getCustomEvaluationRules({
        variables: { parentScenarioId: scenario.scenarioId, limit: 100 }
      });
      if (data?.listCustomEvaluationRules?.items) {
        const sorted = [...data.listCustomEvaluationRules.items].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
        setCustomEvaluationRules(sorted);
      }
    } finally {
      setCustomEvaluationLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose();
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '10px' } }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#FF9800',
          color: 'white',
          fontSize: '18px',
          fontWeight: 400,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <span>カスタム評価ルール管理 - {scenario?.title}</span>
        <Button
          variant="outlined"
          onClick={handleLoadFromFile}
          disabled={customEvaluationLoading}
          sx={{
            color: 'white',
            borderColor: 'white',
            borderRadius: '6px',
            fontSize: '14px',
            '&:hover': { borderColor: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' },
            '&:disabled': { borderColor: 'rgba(255, 255, 255, 0.5)', color: 'rgba(255, 255, 255, 0.5)' }
          }}
        >
          {customEvaluationLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          カスタム評価読込
        </Button>
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {customEvaluationError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCustomEvaluationError(null)}>
            {customEvaluationError}
          </Alert>
        )}
        {customEvaluationLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {customEvaluationRules.length >= 10 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                カスタム評価ルールは1シナリオにつき最大10件まで登録できます。
              </Alert>
            )}
            {customEvaluationRules.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddRule}
                  sx={{
                    bgcolor: '#FF9800',
                    color: 'white',
                    borderRadius: '6px',
                    fontSize: '16px',
                    px: 3,
                    py: 1.5,
                    '&:hover': { bgcolor: '#FF9800', opacity: 0.9 }
                  }}
                >
                  カスタム評価ルールを追加
                </Button>
              </Box>
            ) : (
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                  登録済みカスタム評価ルール ({customEvaluationRules.length}件)
                </Typography>
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="custom-evaluation-rules-list" isDropDisabled={editingRule !== null || isAddingRule}>
                    {(provided) => (
                      <Box {...provided.droppableProps} ref={provided.innerRef}>
                        {customEvaluationRules.map((rule, index) => (
                          <Draggable
                            key={rule.ruleId}
                            draggableId={rule.ruleId}
                            index={index}
                            isDragDisabled={editingRule !== null || isAddingRule}
                          >
                            {(providedRow, snapshot) => (
                              <Paper
                                ref={providedRow.innerRef}
                                {...providedRow.draggableProps}
                                elevation={1}
                                sx={{
                                  p: 2,
                                  mb: 2,
                                  borderRadius: '6px',
                                  border:
                                    editingRule?.ruleId === rule.ruleId ? '2px solid #FF9800' : '1px solid #E0E0E0',
                                  ...(snapshot.isDragging && { bgcolor: '#FFF3E0', boxShadow: '0 4px 8px rgba(0,0,0,0.12)' }),
                                  ...((editingRule !== null || isAddingRule) && { opacity: 0.7 })
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <Box
                                    {...providedRow.dragHandleProps}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      mr: 1,
                                      cursor: editingRule !== null || isAddingRule ? 'not-allowed' : 'grab'
                                    }}
                                  >
                                    <DragIndicatorIcon
                                      sx={{
                                        color: editingRule !== null || isAddingRule ? '#ccc' : '#999',
                                        fontSize: '20px'
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      {rule.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {rule.content}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditRule(rule)}
                                      disabled={editingRule !== null || isAddingRule}
                                      sx={{
                                        bgcolor: '#5D84AF',
                                        color: 'white',
                                        '&:hover': { bgcolor: '#5D84AF', opacity: 0.9 },
                                        '&:disabled': { bgcolor: '#E0E0E0', color: '#9E9E9E' }
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteRule(rule.ruleId)}
                                      disabled={customEvaluationLoading || editingRule !== null || isAddingRule}
                                      sx={{
                                        bgcolor: '#F44336',
                                        color: 'white',
                                        '&:hover': { bgcolor: '#F44336', opacity: 0.9 },
                                        '&:disabled': { bgcolor: '#E0E0E0', color: '#9E9E9E' }
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </Paper>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {customEvaluationRules.length > 0 &&
                          customEvaluationRules.length < 10 &&
                          editingRule === null &&
                          !isAddingRule && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                              <IconButton
                                onClick={handleAddRule}
                                sx={{ bgcolor: '#FF9800', color: 'white', '&:hover': { bgcolor: '#FF9800', opacity: 0.9 } }}
                              >
                                <AddIcon />
                              </IconButton>
                            </Box>
                          )}
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              </Box>
            )}
            {(editingRule !== null || isAddingRule) && (
              <Box sx={{ mt: 3, p: 2, bgcolor: '#F5F5F5', borderRadius: '6px' }}>
                <Typography variant="subtitle2" gutterBottom>
                  {editingRule ? 'カスタム評価ルールを編集' : 'カスタム評価ルールを追加'}
                </Typography>
                <TextField
                  margin="dense"
                  label={`名前（${CUSTOM_EVALUATION_RULE_VALIDATIONS.NAME.MAX_LENGTH}文字まで）`}
                  fullWidth
                  variant="outlined"
                  value={ruleFormData.name}
                  onChange={(e) => setRuleFormData((prev) => ({ ...prev, name: e.target.value }))}
                  inputRef={nameInputRef}
                  placeholder="（例）ヒアリング力"
                  sx={{ mb: 2 }}
                  required={CUSTOM_EVALUATION_RULE_VALIDATIONS.NAME.REQUIRED}
                />
                <TextField
                  margin="dense"
                  label={`内容（${CUSTOM_EVALUATION_RULE_VALIDATIONS.CONTENT.MAX_LENGTH}文字まで）`}
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  value={ruleFormData.content}
                  onChange={(e) => setRuleFormData((prev) => ({ ...prev, content: e.target.value }))}
                  placeholder={'（例）ヒアリング力\n・説明：お客様のニーズや課題を適切に聞き出せているか'}
                  sx={{ mb: 2, '& .MuiInputBase-input': { resize: 'vertical', minHeight: '4em' } }}
                  required={CUSTOM_EVALUATION_RULE_VALIDATIONS.CONTENT.REQUIRED}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveRule}
                    disabled={customEvaluationLoading}
                    sx={{
                      bgcolor: '#FF9800',
                      color: 'white',
                      borderRadius: '6px',
                      '&:hover': { bgcolor: '#FF9800', opacity: 0.9 }
                    }}
                  >
                    {customEvaluationLoading ? <CircularProgress size={24} /> : '保存'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setEditingRule(null);
                      setIsAddingRule(false);
                      setRuleFormData({ name: '', content: '' });
                    }}
                    sx={{
                      color: '#666666',
                      borderColor: '#666666',
                      borderRadius: '6px',
                      '&:hover': { borderColor: '#666666', opacity: 0.9 }
                    }}
                  >
                    キャンセル
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            color: '#666666',
            borderColor: '#666666',
            borderRadius: '6px',
            '&:hover': { borderColor: '#666666', opacity: 0.9 }
          }}
          variant="outlined"
        >
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomEvaluationRulesDialog;
